import { getConfigValue, isBatchPrintEnabledForAnyProduct } from './getConfigValue';
import { focusTypeName } from './constants';

// Single source of truth for the Sales Aid creation wizard's step order and
// skip conditions. Every page's Back/Continue navigation, the entry points
// (HomePage/Navbar/PageContextMenu), and the CreateSalesAidStepProgressBar
// breadcrumb all resolve routes through this module so the three can never
// drift out of sync with each other.

export const SalesAidStepIds = Object.freeze({
    SELECTPRIMARYFOCUS: "selectPrimaryFocus",
    SELECTTERRITORY: "selectTerritory",
    SELECTTARGET: "selectTarget",
    CONFIGUREMESSAGE: "configureMessage",
    GENERATEDOCUMENT: "generateDocument",
    COMPLETED: "completed"
});

export function isHcpOpportunityEnabled(appState) {
    return getConfigValue(appState?.config?.HCP?.Opportunity?.Enabled, appState)?.Value === "true";
}

// The Focus step is only shown when at least one product supports batch print.
export function isFocusStepSkipped(appState) {
    return !isBatchPrintEnabledForAnyProduct(appState);
}

// The Territory step is skipped when the user only has a single territory,
// or when focusing on HCPs with the HCP Opportunity feature enabled.
export function isTerritoryStepSkipped(appState, focusType) {
    return !!appState?.bypassTerritorySelection || (focusType === focusTypeName.HCPs && isHcpOpportunityEnabled(appState));
}

function isStepSkipped(stepId, appState, focusType) {
    switch (stepId) {
        case SalesAidStepIds.SELECTPRIMARYFOCUS:
            return isFocusStepSkipped(appState);
        case SalesAidStepIds.SELECTTERRITORY:
            return isTerritoryStepSkipped(appState, focusType);
        default:
            return false;
    }
}

// The Target step is a different page depending on focus type: HCP search
// (HCPPage) vs. batch-print category target selection (SelectTargetBatchPage).
// Messages also configures its message before picking a target, hence the
// differing step order below.
export function getStepsOrder(focusType) {
    return focusType === focusTypeName.Messages ? [
        SalesAidStepIds.SELECTPRIMARYFOCUS,
        SalesAidStepIds.SELECTTERRITORY,
        SalesAidStepIds.CONFIGUREMESSAGE,
        SalesAidStepIds.SELECTTARGET,
        SalesAidStepIds.GENERATEDOCUMENT,
        SalesAidStepIds.COMPLETED
    ] : [
        SalesAidStepIds.SELECTPRIMARYFOCUS,
        SalesAidStepIds.SELECTTERRITORY,
        SalesAidStepIds.SELECTTARGET,
        SalesAidStepIds.CONFIGUREMESSAGE,
        SalesAidStepIds.GENERATEDOCUMENT,
        SalesAidStepIds.COMPLETED
    ];
}

export function getStepRoute(stepId, focusType) {
    switch (stepId) {
        case SalesAidStepIds.SELECTPRIMARYFOCUS:
            return '/FocusPage';
        case SalesAidStepIds.SELECTTERRITORY:
            return '/TerritoryPage';
        case SalesAidStepIds.SELECTTARGET:
            return focusType === focusTypeName.Messages ? '/SelectTargetBatchPage' : '/HCPPage';
        case SalesAidStepIds.CONFIGUREMESSAGE:
            return '/MessageSelectionPage';
        case SalesAidStepIds.GENERATEDOCUMENT:
            return '/GenerateDocumentPage';
        default:
            return '/RecentSalesAidsPage';
    }
}

// Walks the step order backwards from currentStepId, skipping any step whose
// skip condition applies, and returns the route for the first step that
// isn't skipped. Returns null when every earlier step was skipped, i.e.
// there's nowhere valid to go back to - callers should hide the Back button
// in that case rather than link somewhere the user never saw.
export function getPreviousRoute(currentStepId, appState, focusType) {
    const order = getStepsOrder(focusType);
    const currentIndex = order.indexOf(currentStepId);
    for (let i = currentIndex - 1; i >= 0; i--) {
        if (!isStepSkipped(order[i], appState, focusType)) {
            return getStepRoute(order[i], focusType);
        }
    }
    return null;
}

// Walks the step order forwards from currentStepId. Unlike getPreviousRoute,
// there is always a later non-skipped step to land on (Target and Configure
// Message are never skipped), so this keeps its dashboard fallback.
export function getNextRoute(currentStepId, appState, focusType) {
    const order = getStepsOrder(focusType);
    const currentIndex = order.indexOf(currentStepId);
    for (let i = currentIndex + 1; i < order.length; i++) {
        if (!isStepSkipped(order[i], appState, focusType)) {
            return getStepRoute(order[i], focusType);
        }
    }
    return '/RecentSalesAidsPage';
}

// Where a fresh entry into the flow (HomePage, Navbar, PageContextMenu)
// should land: the first step in the order that isn't skipped.
export function getEntryRoute(appState, focusType) {
    const order = getStepsOrder(focusType);
    for (const stepId of order) {
        if (!isStepSkipped(stepId, appState, focusType)) {
            return getStepRoute(stepId, focusType);
        }
    }
    return '/RecentSalesAidsPage';
}

// Used by "Start a new Sales Aid" from the result page. Restarting normally
// behaves like a fresh entry into the flow; "keep territory/market" is a
// one-off user choice to skip straight to the target/message step for this
// restart only, independent of the configured skip conditions.
export function getRestartRoute(appState, focusType, keepTerritoryAndMarket) {
    if (keepTerritoryAndMarket) {
        return focusType === focusTypeName.Messages ? '/MessageSelectionPage' : '/HCPPage';
    }
    return getEntryRoute(appState, focusType);
}
